import styled from 'styled-components'

interface Props {
  isDoctor?: boolean
}

export const ChatContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
`

/** Fundo cinza-azulado (área de conversa), alinhado a apps de chat. */
export const ChatContent = styled.div`
  height: 500px;
  overflow-y: scroll;
  scroll-behavior: smooth;
  border: 1px solid ${({ theme }) => theme.colors.gray20};
  border-radius: 12px;
  padding: 16px 20px;
`

/** Separador de dia (Hoje / Ontem / data) entre grupos de mensagens. */
export const ChatDayDivider = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 14px 0 10px;
  flex-shrink: 0;
`

export const ChatDayDividerLabel = styled.span`
  display: inline-block;
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.gray70};
  background: ${({ theme }) => theme.colors.gray20};
`

export const ChatBottom = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  flex-wrap: nowrap;
  min-width: 0;
`

export const ChatComposerColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`

export const AudioControlsStack = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`

/** Erro de envio (ex.: mídia) — abaixo da barra de input. */
export const ChatSendError = styled.p`
  margin: 0;
  padding: 4px 0 0;
  font-size: 1.1rem;
  line-height: 1.35;
  color: #c62828;
`

/**
 * Fica no topo da lista: o chat abre só com a página mais recente, e o
 * histórico anterior vem por ação explícita — nunca no carregamento.
 */
export const ChatLoadPrevious = styled.button`
  align-self: center;
  margin: 0 0 8px;
  padding: 6px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 999px;
  background: #ffffff;
  color: #475467;
  font-size: 1.1rem;
  line-height: 1.35;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f2f4f7;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const ChatInputMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px 8px;
  padding: 2px 0 0;
  min-width: 0;
`

export const CharCount = styled.span<{ $atLimit?: boolean }>`
  font-size: 1rem;
  color: ${({ $atLimit }) => ($atLimit ? '#c62828' : '#757575')};
`

export const CharLimitWarning = styled.span`
  font-size: 1rem;
  color: #c62828;
  text-align: right;
  flex: 1;
  min-width: 0;
`

export const AttachButton = styled.button`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray10};
  background: ${({ theme }) => theme.colors.gray20};
  color: ${({ theme }) => theme.colors.dark};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const RecordButton = styled.button<{ $recording?: boolean }>`
  flex-shrink: 0;
  min-width: 44px;
  height: 44px;
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid
    ${({ theme, $recording }) => ($recording ? '#e53935' : theme.colors.gray10)};
  background: ${({ theme, $recording }) =>
    $recording ? 'rgba(229, 57, 53, 0.12)' : theme.colors.gray20};
  color: ${({ $recording }) => ($recording ? '#c62828' : 'inherit')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const RecordTimer = styled.span`
  font-size: 1rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  min-width: 2.5rem;
`

export const AudioPreviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 0 4px;
  min-width: 0;
`

export const AudioProcessingLabel = styled.span`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.gray50};
  font-style: italic;
`

export const AudioPlayerShell = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 100%;
`

export const AudioPreviewPlayer = styled.audio`
  width: 100%;
  min-width: 0;
  max-width: 100%;
  height: 36px;
`

export const AudioPreviewActions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 6px;
`

export const AudioPreviewActionBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray10};
  background: ${({ theme }) => theme.colors.gray20};
  color: ${({ theme }) => theme.colors.dark};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const AudioPreviewSendBtn = styled(AudioPreviewActionBtn)`
  background: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.white};
  border-color: transparent;
`

export const FilePreviewChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 160px;
  padding: 6px 10px;
  background: ${({ theme }) => theme.colors.gray20};
  border-radius: 8px;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.dark};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  button {
    flex-shrink: 0;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.1rem;
    color: ${({ theme }) => theme.colors.gray50};
    padding: 0 2px;
  }
`

export const PendingAttachmentsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  padding: 4px 0 2px;
  max-height: 132px;
  overflow-y: auto;
`

export const PendingAttachmentChip = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 148px;
  min-height: 52px;
  padding: 6px 28px 6px 6px;
  background: ${({ theme }) => theme.colors.gray20};
  border: 1px solid ${({ theme }) => theme.colors.gray10};
  border-radius: 10px;
`

export const PendingAttachmentThumb = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
`

export const PendingAttachmentIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.gray50};
`

export const PendingAttachmentMeta = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
`

export const PendingAttachmentName = styled.span`
  font-size: 1.05rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.dark};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const PendingAttachmentMime = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.gray50};
  text-transform: uppercase;
`

export const PendingAttachmentRemove = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
  color: ${({ theme }) => theme.colors.gray50};
  cursor: pointer;

  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  :hover:not(:disabled) {
    background: rgba(198, 40, 40, 0.12);
    color: #c62828;
  }
`

export const ChatInput = styled.textarea`
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 44px;
  max-height: 44px;
  border: none;
  border: 1px solid ${({ theme }) => theme.colors.gray10};
  padding: 10px;
  border-radius: 8px;
  resize: none;
`

export const ChatButton = styled.button<{ $recording?: boolean }>`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  border: none;
  background: ${({ theme, $recording }) =>
    $recording ? '#e53935' : theme.colors.secondary};
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

// Message
export const MessageContainer = styled.div<Props>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: ${({ isDoctor }) => (isDoctor ? 'flex-end' : 'flex-start')};
  margin-bottom: 14px;
  max-width: 100%;
`

export const MessageName = styled.div<Props>`
  font-size: 1.3rem;
  line-height: 1.35;
  font-weight: 500;
  text-align: ${({ isDoctor }) => (isDoctor ? 'right' : 'left')};
  align-self: ${({ isDoctor }) => (isDoctor ? 'flex-end' : 'flex-start')};
  max-width: 78%;
  margin-bottom: 6px;
`

export const MessageRoleText = styled.span`
  color: ${({ theme }) => theme.colors.secondary};
`

export const MessageNameParens = styled.span`
  color: ${({ theme }) => theme.colors.dark};
  font-weight: 500;
`

export const MessageNameSingle = styled.span`
  color: ${({ theme }) => theme.colors.dark};
  font-weight: 500;
`

type MessageContentExtra = Props & { $fitWidth?: boolean }

export const MessageContent = styled.div<MessageContentExtra>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px 10px 14px;
  max-width: 80%;
  min-width: 0;
  background: ${({ theme }) => theme.colors.secondaryLight};
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(44, 43, 67, 0.06);
  align-self: ${({ isDoctor }) => (isDoctor ? 'flex-end' : 'flex-start')};
  align-items: ${({ $fitWidth, isDoctor }) => {
    if (!$fitWidth) return 'stretch'
    /* Mídia: horário fica abaixo da imagem; paciente alinha à esquerda da bolha. */
    return isDoctor ? 'flex-end' : 'flex-start'
  }};
  ${({ $fitWidth }) => ($fitWidth ? 'width: fit-content;' : '')}
`

export const MessageDeleteButton = styled.button<Props>`
  position: absolute;
  top: 6px;
  ${({ isDoctor }) => (isDoctor ? 'right: 6px;' : 'left: 6px;')}
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.85);
  color: ${({ theme }) => theme.colors.gray50};
  cursor: pointer;
  opacity: 0;
  transition:
    opacity 0.15s ease,
    background 0.15s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  ${MessageContent}:hover &,
  ${MessageContent}:focus-within & {
    opacity: 1;
  }

  :hover:not(:disabled) {
    background: rgba(255, 255, 255, 1);
    color: #c62828;
  }

  :disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

export const MessageTextBlock = styled.div<Props>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  width: 100%;
  min-width: 0;
`

export const MessageTextBody = styled.div`
  min-width: 0;
  font-size: 1.35rem;
  line-height: 1.45;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.dark};
  word-break: break-word;
`

export const MessageTimeInline = styled.span<Props>`
  flex: 0 0 auto;
  font-size: 0.8rem !important;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.gray50};
  white-space: nowrap;
  align-self: ${({ isDoctor }) => (isDoctor ? 'flex-end' : 'flex-start')};
`

export const MessageCaptionOnly = styled.div`
  font-size: 1.3rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.dark};
  word-break: break-word;
  width: 100%;
  min-width: 0;
  align-self: stretch;
  margin-bottom: 2px;
`

export const FileAttachmentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  min-width: 220px;
  align-self: stretch;
`

export const FileAttachmentRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  min-width: 0;
`

export const FileIconCircle = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
`

export const FileIconImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export const FileMetaCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`

export const FileTitle = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark};
  line-height: 1.3;
  word-break: break-word;
`

export const FileSizeLabel = styled.span`
  font-size: 1rem !important;
  font-weight: normal;
  color: ${({ theme }) => theme.colors.gray30};
`

export const FileDownloadAction = styled.a`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  :hover {
    background: rgba(0, 0, 0, 0.06);
    color: ${({ theme }) => theme.colors.secondary};
  }
  &.disabled {
    pointer-events: none;
    opacity: 0.45;
  }
`

export const FileCardFooter = styled.div<Props>`
  display: flex;
  justify-content: ${({ isDoctor }) => (isDoctor ? 'flex-end' : 'flex-start')};
  width: 100%;
`

export const FileCardTime = styled.div`
  font-size: 0.8rem !important;
  color: ${({ theme }) => theme.colors.gray50};
  margin-top: 2px;
`

export const AudioAttachmentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  min-width: 200px;
  max-width: min(320px, 100%);
`

export const AudioMessagePlayer = styled.audio`
  width: 100%;
  min-width: 180px;
  max-width: 100%;
  height: 40px;
`

/** Pré-visualização de imagem — encolhe junto com a mídia (sem faixa vazia na bolha). */
export const ImageBubbleRoot = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: fit-content;
  max-width: 100%;
`

export const ImageBubbleFrame = styled.div`
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.gray20};
  box-shadow: 0 1px 3px rgba(44, 43, 67, 0.12);
`

export const ImageBubbleOpenButton = styled.button`
  display: block;
  width: fit-content;
  max-width: 100%;
  padding: 0;
  margin: 0;
  border: none;
  cursor: zoom-in;
  background: transparent;
  line-height: 0;
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 2px;
  }
`

export const ImageBubbleImg = styled.img`
  width: auto;
  max-height: 220px;
  height: auto;
  object-fit: contain;
  display: block;
`

export const ImageBubbleActions = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 6px;
  z-index: 1;
  pointer-events: none;
  & > * {
    pointer-events: auto;
  }
`

export const ImageBubbleIconBtn = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.94);
  color: ${({ theme }) => theme.colors.secondary};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  transition: background 0.15s ease;
  &:hover {
    background: ${({ theme }) => theme.colors.white};
  }
`

export const ImageBubbleMediaDownload = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font: inherit;
  background: rgba(255, 255, 255, 0.94);
  color: ${({ theme }) => theme.colors.secondary};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  transition: background 0.15s ease;
  &:hover {
    background: ${({ theme }) => theme.colors.white};
  }
`

export const ImageBubbleMeta = styled.div<Props>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 2px 0;
  max-width: 100%;
  align-items: ${({ isDoctor }) => (isDoctor ? 'flex-start' : 'flex-end')};
`

export const ImageBubbleFileName = styled.span`
  font-size: 1.05rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gray50};
  word-break: break-all;
`

export const ImageLightboxBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: rgba(0, 0, 0, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
`

export const ImageLightboxPanel = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  max-width: min(960px, 96vw);
  max-height: 92vh;
  background: #1a1a1a;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
`

export const ImageLightboxToolbar = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.55);
`

export const ImageLightboxTitle = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 1.2rem;
  color: #f5f5f5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const ImageLightboxIconBtn = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`

export const ImageLightboxDownloadLink = styled.a`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`

export const ImageLightboxDownloadBtn = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font: inherit;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`

export const ImageLightboxBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px 14px;
  overflow: auto;
`

export const ImageLightboxImg = styled.img`
  max-width: 100%;
  max-height: min(80vh, 820px);
  object-fit: contain;
  border-radius: 4px;
`

export const QuickMessagesContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 10px;
  padding: 10px 0;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background: ${({ theme }) => theme.colors.gray30};
  }
`

export const QuickMessageButton = styled.button`
  flex-shrink: 0;
  white-space: nowrap;
  padding: 8px 16px;
  font-size: 1.2rem !important;
  font-weight: normal !important;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.secondaryLight};
  cursor: pointer;
`
